/**
 * VIDEOS OF IT, on its page: a software demo, a walkthrough, a tour of a
 * place, a review. Found in a search and checked against it
 * (`boardroom.videos`), so each one was in a real search result.
 *
 * NOTHING LOADS FROM YOUTUBE UNTIL ONE IS CLICKED. A page with six videos
 * would otherwise load six players; until then each is a thumbnail. The player
 * is YouTube's no-cookie one, which sets nothing until the video plays.
 */
import { useState } from 'react';
import type { BoardroomVideo } from '../../../types';

const KIND_WORDS: Record<string, string> = {
  demo: 'demo',
  walkthrough: 'walkthrough',
  tour: 'tour',
  review: 'review',
};

const UNIT_WORDS: Record<string, string> = {
  y: 'year', yr: 'year', year: 'year', mo: 'month', month: 'month', w: 'week', wk: 'week', week: 'week',
  d: 'day', day: 'day', h: 'hour', hr: 'hour', hour: 'hour', min: 'minute', minute: 'minute', s: 'second', sec: 'second', second: 'second',
};

/** "7mo ago" as a person says it: "about 7 months ago". YouTube shows an
 *  age, not a date, so it is said as roughly as it is known. */
export function ageWords(age: string | null | undefined): string {
  const m = /(\d+)\s*([a-z]+?)s?\s+ago/i.exec(age ?? '');
  if (!m) return '';
  const n = Number(m[1]);
  const unit = UNIT_WORDS[m[2].toLowerCase()];
  return unit ? `about ${n} ${unit}${n === 1 ? '' : 's'} ago` : '';
}

export function embedUrl(v: Pick<BoardroomVideo, 'video_id'>): string {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(v.video_id)}?autoplay=1&rel=0`;
}

export function thumbUrl(v: Pick<BoardroomVideo, 'video_id'>): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(v.video_id)}/hqdefault.jpg`;
}

export default function Videos({
  videos,
  name,
  onRuleOut,
}: {
  videos: BoardroomVideo[];
  /** What they are videos of, for the heading. */
  name: string;
  /** "Not this one": the video is ruled out and leaves the page. */
  onRuleOut?: (videoId: number) => void;
}) {
  const [playing, setPlaying] = useState<number | null>(null);
  if (!videos.length) return null;
  return (
    <section
      className="shrink-0 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
      data-testid="profile-videos"
    >
      <h3 className="mb-2 text-[11px] uppercase tracking-wide text-gray-400">
        Videos of {name} · {videos.length} <span className="normal-case tracking-normal">· newest first</span>
      </h3>
      {/* ONE ROW THAT SCROLLS, newest at the front: the room keeps finding
          more, and a grid that grew down the page pushed everything else off. */}
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="video-row">
        {videos.map(v => (
          <figure key={v.id} className="w-64 shrink-0" data-testid={`video-${v.id}`}>
            <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900">
              {playing === v.id ? (
                <iframe
                  src={embedUrl(v)}
                  title={v.title}
                  className="absolute inset-0 h-full w-full"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  data-testid={`video-player-${v.id}`}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(v.id)}
                  className="group absolute inset-0 h-full w-full"
                  aria-label={`Play ${v.title}`}
                  data-testid={`video-play-${v.id}`}
                >
                  <img src={thumbUrl(v)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-full bg-black/60 px-3 py-1.5 text-sm text-white group-hover:bg-black/80">
                      ▶
                    </span>
                  </span>
                </button>
              )}
            </div>
            <figcaption className="mt-1 text-xs">
              <div className="flex items-start gap-1.5">
                {KIND_WORDS[v.kind] && (
                  <span className="shrink-0 rounded bg-gray-100 px-1 text-[11px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {KIND_WORDS[v.kind]}
                  </span>
                )}
                <span className="line-clamp-2 min-w-0 text-gray-800 dark:text-gray-100">{v.title}</span>
              </div>
              {(v.channel || ageWords(v.age)) && (
                <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400" title={v.published ? `Went up around ${v.published}` : undefined}>
                  {[v.channel, ageWords(v.age)].filter(Boolean).join(' · ')}
                </div>
              )}
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
                {v.url && (
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    open on YouTube
                  </a>
                )}
                {onRuleOut && (
                  <button
                    type="button"
                    onClick={() => onRuleOut(v.id)}
                    className="hover:text-red-600"
                    title="Not a video of this - take it off the page"
                    data-testid={`video-rule-out-${v.id}`}
                  >
                    not this one
                  </button>
                )}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
